import { useState } from 'react';

interface TagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
}

// Lightweight chip-style input. Pressing Enter or comma adds a tag; Backspace
// in an empty input removes the last tag. Suggestions filter as you type.
export function TagInput({ value, onChange, suggestions = [], placeholder }: TagInputProps) {
  const [draft, setDraft] = useState('');
  const lower = draft.trim().toLowerCase();
  const filtered = lower
    ? suggestions
        .filter((s) => s.toLowerCase().includes(lower) && !value.includes(s))
        .slice(0, 6)
    : [];

  function add(tag: string) {
    const cleaned = tag.trim();
    if (!cleaned) return;
    if (value.includes(cleaned)) return;
    onChange([...value, cleaned]);
    setDraft('');
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-ink-900/15 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-ink-900/10">
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-canvas-100 px-2.5 py-1 text-xs font-medium text-ink-900">
            #{t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => remove(t)}
              className="rounded-full text-ink-500 hover:text-ink-900"
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="min-w-[8rem] flex-1 bg-transparent px-2 py-1 text-sm focus:outline-none"
          value={draft}
          placeholder={placeholder ?? 'Add a tag…'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              remove(value[value.length - 1]);
            }
          }}
          onBlur={() => draft && add(draft)}
        />
      </div>
      {filtered.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {filtered.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => add(s)}
              className="rounded-full border border-ink-900/10 bg-canvas-50 px-2.5 py-1 text-xs text-ink-700 hover:bg-canvas-100"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
